/**
 * Background Location Enrichment Service.
 * Runs as a batch job on app load, enriching properties with nearby POI info.
 * Designed to run 1-2x/day — results cached in localStorage with 12h TTL.
 */

import { llmConfigured, analyzePropertyRisk } from './llmService';

const ENRICHMENT_CACHE_KEY = 'lupaleilao_location_cache';
const ENRICHMENT_META_KEY = 'lupaleilao_enrichment_meta';
const CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours
const BATCH_DELAY_MS = 2000; // 2s between calls to not overload local LLM

function readCache() {
  try { return JSON.parse(localStorage.getItem(ENRICHMENT_CACHE_KEY) || '{}'); } catch { return {}; }
}

function writeCache(cache) {
  localStorage.setItem(ENRICHMENT_CACHE_KEY, JSON.stringify(cache));
}

function getMeta() {
  try { return JSON.parse(localStorage.getItem(ENRICHMENT_META_KEY) || '{}'); } catch { return {}; }
}

function setMeta(meta) {
  localStorage.setItem(ENRICHMENT_META_KEY, JSON.stringify(meta));
}

/** Get enrichment data for a property (returns null if not cached/expired) */
export function getEnrichment(propertyId) {
  const cache = readCache();
  const entry = cache[propertyId];
  if (!entry) return null;
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) return null;
  return entry.data;
}

/** Get set of all enriched property IDs (non-expired) */
export function getEnrichedIds() {
  const cache = readCache();
  const ids = new Set();
  const now = Date.now();
  for (const [id, entry] of Object.entries(cache)) {
    if (now - entry.timestamp <= CACHE_TTL_MS) ids.add(Number(id));
  }
  return ids;
}

/** Check if the batch job should run (hasn't run in the last 12h) */
function shouldRunBatch() {
  const meta = getMeta();
  if (!meta.lastRun) return true;
  return (Date.now() - meta.lastRun) > CACHE_TTL_MS;
}

/**
 * Start the background enrichment job.
 * Processes properties sequentially (1 at a time) with a delay between each.
 * Skips already-enriched properties.
 * @param {Array} properties - All properties to potentially enrich
 * @param {Function} onUpdate - Called after each property is enriched with updated IDs set
 * @returns {Promise<void>}
 */
export async function startEnrichmentJob(properties, onUpdate) {
  if (!shouldRunBatch()) {
    console.log('[Enrichment] Skipping — last run was recent');
    return;
  }

  if (!llmConfigured) {
    console.log('[Enrichment] No LLM API key configured — skipping batch');
    return;
  }

  console.log('[Enrichment] Starting background enrichment job...');
  setMeta({ lastRun: Date.now(), status: 'running' });

  const cache = readCache();
  const now = Date.now();

  // Filter to properties that need enrichment (not cached or expired)
  const toProcess = properties.filter(p => {
    const entry = cache[p.id];
    return !entry || (now - entry.timestamp > CACHE_TTL_MS);
  });

  if (toProcess.length === 0) {
    console.log('[Enrichment] All properties already enriched');
    setMeta({ lastRun: Date.now(), status: 'done', count: 0 });
    return;
  }

  let processed = 0;
  for (const p of toProcess) {
    try {
      const data = await analyzePropertyRisk(p);
      const currentCache = readCache();
      currentCache[p.id] = { data, timestamp: Date.now() };
      writeCache(currentCache);
      processed++;
      onUpdate?.(getEnrichedIds());
    } catch (err) {
      console.warn(`[Enrichment] Failed for property ${p.id}:`, err.message);
    }

    // Delay between calls
    if (toProcess.indexOf(p) < toProcess.length - 1) {
      await new Promise(resolve => setTimeout(resolve, BATCH_DELAY_MS));
    }
  }

  setMeta({ lastRun: Date.now(), status: 'done', count: processed });
  console.log(`[Enrichment] Done. Enriched ${processed}/${toProcess.length} properties.`);
}
